import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Workflow, RunInstance, WorkflowStep } from '../../types';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ChatViewClearButton from './ChatViewClearButton';
import { useAssistantChatStore } from '../../store/assistantChatStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { assistantChatService } from '../../services/assistantChatService';
import apiService from '../../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  timestamp: string;
  tool_calls?: any[];
  suggestedActions?: any[];
  metadata?: any;
}

interface ChatInterfaceProps {
  workflow: Workflow;
  runInstance: RunInstance;
  assistantStep: WorkflowStep;
  onMessageSend: (message: string) => void;
  sessionConfig?: {
    mode?: 'isolated' | 'shared' | 'run';
    enabled?: boolean;
  };
  className?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  workflow,
  runInstance,
  assistantStep,
  onMessageSend,
  sessionConfig,
  className = ''
}) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const latestBuildIdRef = useRef<number>(0);
  const sessionClearedRef = useRef<boolean>(false);

  const {
    addMessage,
    setMessages,
    clearMessages,
    isTyping,
    setIsTyping,
    setActiveSession,
    createSession,
    deleteSession,
    getMessages,
    messagesBySession,
    activeSessionKey
  } = useAssistantChatStore();
  
  // Subscribe to messages changes by watching the relevant state
  const messages = useAssistantChatStore((state) => {
    const key = state.activeSessionKey;
    if (!key) return [];
    return state.messagesBySession[key] || [];
  });

  // Get workflow execution state to check if workflow is running
  const execution = useWorkflowStore((state) =>
    state.executions.get(workflow.orchestration_id.toString())
  );

  // Consider workflow running if status is 'running' or 'paused'
  // Explicitly exclude 'stopped', 'completed', 'failed', and 'pending' statuses
  const isWorkflowRunning =
    execution?.status === 'running' ||
    execution?.status === 'paused';

  // Debug: Log workflow running state
  useEffect(() => {
    console.log('[DEBUG] ChatInterface - workflow running state:', {
      isWorkflowRunning,
      executionIsRunning: execution?.isRunning,
      executionStatus: execution?.status,
      hasRunInstance: !!execution?.runInstance,
      runInstanceId: execution?.runInstance?.run_instance_id
    });
  }, [isWorkflowRunning, execution?.isRunning, execution?.status, execution?.runInstance]);

  // Debug: Log messages whenever they change
  useEffect(() => {
    console.log('[DEBUG] ChatInterface - messages changed:', messages);
    console.log('[DEBUG] ChatInterface - messages.length:', messages.length);
  }, [messages]);

  // Create and set active session for this chat
  useEffect(() => {
    // For assistant intent steps, always use 'shared' mode to maintain conversation history across runs
    const sessionMode = sessionConfig?.mode || 'shared';
    console.log('[DEBUG] ChatInterface - Creating session with mode:', sessionMode, 'sessionConfig:', sessionConfig);

    // Clear any existing session that might have the old format
    const oldFormatKey = `${workflow.orchestration_id}-${runInstance.run_instance_id}-${assistantStep.orchestration_step_id}`;
    console.log('[DEBUG] ChatInterface - Clearing old format session key if exists:', oldFormatKey);
    deleteSession(oldFormatKey);

    const sessionKey = createSession(
      Number(workflow.orchestration_id),
      Number(runInstance.run_instance_id),
      Number(assistantStep.orchestration_step_id),
      sessionMode
    );
    console.log('[DEBUG] ChatInterface - Created session key:', sessionKey);
    setActiveSession(sessionKey);
  }, [workflow.orchestration_id, runInstance.run_instance_id, assistantStep.orchestration_step_id, sessionConfig?.mode, createSession, deleteSession, setActiveSession]);

  // Listen for session cleared events to prevent auto-reload from restoring messages
  useEffect(() => {
    const handleSessionCleared = (event: Event) => {
      const customEvent = event as CustomEvent<{ sessionKey: string }>;
      const clearedSessionKey = customEvent.detail?.sessionKey;
      console.log('[DEBUG] ChatInterface - Received sessionCleared event for key:', clearedSessionKey);

      // Check if this is the active session
      if (clearedSessionKey === activeSessionKey) {
        console.log('[DEBUG] ChatInterface - Setting sessionClearedRef to true to prevent auto-reload');
        sessionClearedRef.current = true;

        // Reset the flag after 10 seconds (in case user wants to start new conversation)
        setTimeout(() => {
          console.log('[DEBUG] ChatInterface - Resetting sessionClearedRef to false');
          sessionClearedRef.current = false;
        }, 10000);
      }
    };

    window.addEventListener('sessionCleared', handleSessionCleared);
    return () => {
      window.removeEventListener('sessionCleared', handleSessionCleared);
    };
  }, [activeSessionKey]);

  const loadConversationHistory = useCallback(async () => {
    try {
      // If session was explicitly cleared, don't reload from previous runs
      if (sessionClearedRef.current) {
        console.log('[DEBUG] ChatInterface - Session was cleared, skipping load from previous runs');
        // Still load current run results to show new messages, but don't look at previous runs
      }

      const buildId = Date.now();
      latestBuildIdRef.current = buildId;
      console.log('[DEBUG] ChatInterface - Loading conversation history for run instance:', runInstance.run_instance_id);

      // Get run results with session messages included
      const response = await apiService.getRunInstanceResults(runInstance.run_instance_id.toString(), true);
      console.log('[DEBUG] ChatInterface - API returned response:', response);
      console.log('[DEBUG] ChatInterface - Run results length:', response.results.length);
      console.log('[DEBUG] ChatInterface - Session messages length:', response.session_messages.length);

      const runResults = response.results;

      // If session was cleared, only use current run results (no previous runs)
      if (sessionClearedRef.current) {
        console.log('[DEBUG] ChatInterface - Building from current run only (session was cleared)');
        await buildConversationFromRunResults(runResults, response.session_messages);
        return;
      }

      // If current run has no assistant conversation yet, try to load from previous runs
      const hasAssistantConversation = runResults.some(result =>
        result.step_type === 'run_agent' &&
        result.orchestration_step_id === assistantStep.orchestration_step_id &&
        result.final_output
      );

      if (!hasAssistantConversation) {
        console.log('[DEBUG] ChatInterface - No assistant conversation in current run, looking for previous runs');

        // Try to get conversation history from previous run instances
        // We'll look for the most recent run that had assistant conversation
        for (let i = 1; i <= 10; i++) {
          const previousRunId = Number(runInstance.run_instance_id) - i;
          if (previousRunId > 0) {
            try {
              console.log('[DEBUG] ChatInterface - Checking previous run instance:', previousRunId);
              const previousResponse = await apiService.getRunInstanceResults(previousRunId.toString(), true);
              const previousRunResults = previousResponse.results;

              // Check if this previous run had assistant conversation
              const previousAssistantResults = previousRunResults.filter(result =>
                result.step_type === 'run_agent' &&
                result.orchestration_step_id === assistantStep.orchestration_step_id &&
                result.final_output
              );

              if (previousAssistantResults.length > 0) {
                console.log('[DEBUG] ChatInterface - Found assistant conversation in previous run:', previousRunId);
                // Use the previous run's results to build conversation history
                const allRunResults = [...previousRunResults, ...runResults];
                // Combine session messages from both runs (previous run might have more complete session data)
                const combinedSessionMessages = [...previousResponse.session_messages, ...response.session_messages];
                await buildConversationFromRunResults(allRunResults, combinedSessionMessages);
                return;
              }
            } catch (error) {
              console.log('[DEBUG] ChatInterface - Error loading previous run:', previousRunId, error);
              // Continue to next run
            }
          }
        }
      }

      // Build conversation from current run results with session messages
      await buildConversationFromRunResults(runResults, response.session_messages);
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  }, [runInstance.run_instance_id, assistantStep.orchestration_step_id, setMessages]);

  const buildConversationFromRunResults = useCallback(async (runResults: any[], sessionMessages: any[] = []) => {
    try {
      console.log('[DEBUG] ChatInterface - Building conversation from run results and session messages');
      console.log('[DEBUG] ChatInterface - Run results count:', runResults.length);
      console.log('[DEBUG] ChatInterface - Session messages count:', sessionMessages.length);
      
      // Filter for user_prompt and run_agent steps related to this assistant
      const conversationSteps = runResults.filter(result => 
        (result.step_type === 'user_prompt') || 
        (result.step_type === 'run_agent' && result.orchestration_step_id === assistantStep.orchestration_step_id && result.final_output)
      ).sort((a, b) => a.step_order - b.step_order);
      
      console.log('[DEBUG] ChatInterface - conversationSteps after filtering:', JSON.stringify(conversationSteps, null, 2));
      
      // Convert session messages and run results to chat messages with deduplication
      const messages: Message[] = [];
      let messageId = 1;
      let hasDisplayedInitialGreeting = false;
      
      // Helper function to extract clean text content from structured content
      const extractCleanText = (content: string): string => {
        console.log('[DEBUG] ChatInterface - extractCleanText called with:', content);
        if (typeof content === 'string' && content.startsWith('[') && content.includes('output_text')) {
          console.log('[DEBUG] ChatInterface - Content matches structured format, parsing...');
          try {
            // Replace single quotes with double quotes for valid JSON parsing
            const jsonString = content.replace(/'/g, '"');
            console.log('[DEBUG] ChatInterface - Converted JSON string:', jsonString);
            const parsedContent = JSON.parse(jsonString);
            if (Array.isArray(parsedContent)) {
              const textContent = parsedContent.find((c: any) => c.type === 'output_text' || c.type === 'text');
              const result = textContent ? textContent.text : content;
              console.log('[DEBUG] ChatInterface - Extracted text:', result);
              return result;
            }
          } catch (e) {
            console.log('[DEBUG] ChatInterface - Failed to parse content:', e);
            // If parsing fails, return original content
          }
        }
        console.log('[DEBUG] ChatInterface - Returning original content:', content);
        return content;
      };
      
      // First, add session messages (these represent the persistent conversation history)
      // Session messages are the authoritative source for conversation history
      for (const sessionMsg of sessionMessages) {
        // Parse content to extract text from structured content
        let content: string = '';
        if (sessionMsg.content) {
          content = extractCleanText(sessionMsg.content);
        } else if (sessionMsg.content_json) {
          const jsonVal: any = sessionMsg.content_json;
          try {
            if (Array.isArray(jsonVal)) {
              const textItem = jsonVal.find((c: any) => c?.type === 'output_text' || c?.type === 'text');
              if (textItem && typeof textItem.text === 'string') {
                content = textItem.text;
              } else if (textItem && typeof textItem.content === 'string') {
                content = textItem.content;
              } else {
                content = JSON.stringify(jsonVal);
              }
            } else if (typeof jsonVal === 'object' && jsonVal !== null) {
              if (Array.isArray(jsonVal.content)) {
                const textItem = jsonVal.content.find((c: any) => c?.type === 'output_text' || c?.type === 'text');
                if (textItem && typeof textItem.text === 'string') {
                  content = textItem.text;
                } else if (textItem && typeof textItem.content === 'string') {
                  content = textItem.content;
                } else {
                  content = JSON.stringify(jsonVal);
                }
              } else if (typeof jsonVal.text === 'string') {
                content = jsonVal.text;
              } else if (typeof jsonVal.message === 'string') {
                content = jsonVal.message;
              } else if (typeof jsonVal.content === 'string') {
                content = jsonVal.content;
              } else {
                content = JSON.stringify(jsonVal);
              }
            } else {
              content = String(jsonVal);
            }
          } catch (err) {
            console.log('[DEBUG] ChatInterface - Failed to parse session content_json, falling back to string', err);
            content = JSON.stringify(jsonVal);
          }
        } else {
          content = '';
        }
        
        messages.push({
          id: `session-${messageId++}`,
          role: sessionMsg.role,
          content: content,
          timestamp: sessionMsg.created_at || new Date().toISOString()
        });
      }
      
      // If we have session messages, use them as the complete conversation history
      // Session messages are the authoritative source - no need to add run results
      if (sessionMessages.length > 0) {
        hasDisplayedInitialGreeting = true;
        console.log('[DEBUG] ChatInterface - Using session messages as complete conversation history');

        // Also include any run results that are newer than session (avoid flicker when session is slightly behind)
        // Helper to check if a message exists already
        const existsInMessages = (role: string, content: string) =>
          messages.some(m => m.role === role && m.content === content);

        // Sort relevant steps chronologically
        const sortedSteps = [...conversationSteps].sort((a, b) => {
          const timeA = new Date(a.created_at || 0).getTime();
          const timeB = new Date(b.created_at || 0).getTime();
          return timeA - timeB;
        });

        for (const step of sortedSteps) {
          if (step.step_type === 'user_prompt') {
            if (step.final_output) {
              const userContent = typeof step.final_output === 'string'
                ? step.final_output
                : JSON.stringify(step.final_output);
              if (!existsInMessages('user', userContent)) {
                messages.push({
                  id: `user-${messageId++}`,
                  role: 'user',
                  content: userContent,
                  timestamp: step.updated_at || step.created_at || new Date().toISOString()
                });
              }
            }
          } else if (step.step_type === 'run_agent') {
            if (step.as_input_list_items && step.as_input_list_items.length > 0) {
              for (const item of step.as_input_list_items) {
                if (item.item_data && item.item_data.role && item.item_data.content) {
                  const itemData = item.item_data;
                  let content = '';
                  if (typeof itemData.content === 'string') {
                    content = itemData.content;
                  } else if (Array.isArray(itemData.content)) {
                    const textContent = itemData.content.find((c: any) => c.type === 'output_text' || c.type === 'text');
                    content = textContent ? textContent.text : JSON.stringify(itemData.content);
                  } else {
                    content = JSON.stringify(itemData.content);
                  }
                  if (!existsInMessages(itemData.role, content)) {
                    messages.push({
                      id: `conversation-${messageId++}`,
                      role: itemData.role,
                      content: content,
                      timestamp: item.created_at || step.created_at || new Date().toISOString()
                    });
                  }
                }
              }
            } else if (step.final_output) {
              let content = '';
              if (typeof step.final_output === 'string') {
                content = step.final_output;
              } else if (step.final_output && typeof step.final_output === 'object') {
                const output = step.final_output as any;
                if (output.content) {
                  content = output.content;
                } else if (output.message) {
                  content = output.message;
                } else if (Array.isArray(output)) {
                  const textContent = output.find((c: any) => c.type === 'output_text' || c.type === 'text');
                  content = textContent ? textContent.text : JSON.stringify(step.final_output);
                } else {
                  content = JSON.stringify(step.final_output);
                }
              } else {
                content = JSON.stringify(step.final_output);
              }
              if (!existsInMessages('assistant', content)) {
                messages.push({
                  id: `assistant-${messageId++}`,
                  role: 'assistant',
                  content: content,
                  timestamp: step.created_at || new Date().toISOString()
                });
              }
            }
          }
        }
        // Session messages (with possible run-result deltas) handled; continue to set below
      } else {
        // No session messages, build conversation from run results
        console.log('[DEBUG] ChatInterface - No session messages, building from run results');
        
        // Sort conversation steps by timestamp to ensure chronological order
        const sortedSteps = [...conversationSteps].sort((a, b) => {
          const timeA = new Date(a.created_at || 0).getTime();
          const timeB = new Date(b.created_at || 0).getTime();
          return timeA - timeB;
        });
        
        // Build conversation from run results
        for (const step of sortedSteps) {
          if (step.step_type === 'user_prompt') {
            // For user_prompt steps in assistant workflows:
            // - If final_output exists, it's the user's response
            // - If final_output is null, only show as assistant greeting if it's the first one
            if (step.final_output) {
              // User has provided input
              const userContent = typeof step.final_output === 'string' ? step.final_output : JSON.stringify(step.final_output);
              messages.push({
                id: `user-${messageId++}`,
                role: 'user',
                content: userContent,
                timestamp: step.created_at || new Date().toISOString()
              });
            } else if (!hasDisplayedInitialGreeting) {
              // This is the initial assistant's greeting message (waiting for user input)
              messages.push({
                id: `assistant-${messageId++}`,
                role: 'assistant',
                content: step.input_message || 'Hello! How can I help you today?',
                timestamp: step.created_at || new Date().toISOString()
              });
              hasDisplayedInitialGreeting = true;
            }
            // If final_output is null and we've already displayed the initial greeting,
            // don't show anything - this is just waiting for user input
          } else if (step.step_type === 'run_agent') {
            // For run_agent steps, add messages from run results
            
            if (step.as_input_list_items && step.as_input_list_items.length > 0) {
              console.log('[DEBUG] ChatInterface - Processing as_input_list_items:', step.as_input_list_items.length, 'items');
              
              for (const item of step.as_input_list_items) {
                if (item.item_data && item.item_data.role && item.item_data.content) {
                  const itemData = item.item_data;
                  
                  // Extract content from the item data
                  let content = '';
                  if (typeof itemData.content === 'string') {
                    content = itemData.content;
                  } else if (Array.isArray(itemData.content)) {
                    // Handle structured content (like OpenAI message format)
                    const textContent = itemData.content.find((c: any) => c.type === 'output_text' || c.type === 'text');
                    content = textContent ? textContent.text : JSON.stringify(itemData.content);
                  } else {
                    content = JSON.stringify(itemData.content);
                  }
                  
                  messages.push({
                    id: `conversation-${messageId++}`,
                    role: itemData.role,
                    content: content,
                    timestamp: item.created_at || step.created_at || new Date().toISOString()
                  });
                  
                  // Mark that we've displayed conversation content
                  if (itemData.role === 'assistant') {
                    hasDisplayedInitialGreeting = true;
                  }
                }
              }
            } else if (step.final_output) {
              // Only use final_output if there are no as_input_list_items
              let content = '';
              if (typeof step.final_output === 'string') {
                content = step.final_output;
              } else if (step.final_output && typeof step.final_output === 'object') {
                const output = step.final_output as any;
                if (output.content) {
                  content = output.content;
                } else if (output.message) {
                  content = output.message;
                } else if (Array.isArray(output)) {
                  // Handle structured content array (like OpenAI message format)
                  const textContent = output.find((c: any) => c.type === 'output_text' || c.type === 'text');
                  content = textContent ? textContent.text : JSON.stringify(step.final_output);
                } else {
                  content = JSON.stringify(step.final_output);
                }
              } else {
                content = JSON.stringify(step.final_output);
              }
              
              messages.push({
                id: `assistant-${messageId++}`,
                role: 'assistant',
                content: content,
                timestamp: step.created_at || new Date().toISOString()
              });
              hasDisplayedInitialGreeting = true; // Mark that we've displayed an assistant message
            }
        }
        }
      }
      
      console.log('[DEBUG] ChatInterface - Final messages to set:', JSON.stringify(messages, null, 2));
      setMessages(messages);
    } catch (error) {
      console.error('Failed to build conversation from run results:', error);
    }
  }, [workflow.orchestration_id, runInstance.run_instance_id, assistantStep.orchestration_step_id, activeSessionKey, setMessages]);

  // Load conversation history from run results
  // This will be triggered when the component mounts or when dependencies change
  useEffect(() => {
    loadConversationHistory();
  }, [loadConversationHistory]);

  // Reload conversation history when workflow store run results change
  // But only if we don't already have messages (to avoid refreshing and losing conversation)
  useEffect(() => {
    const unsubscribe = useWorkflowStore.subscribe((state) => {
      const execution = state.executions.get(workflow.orchestration_id.toString());
      if (execution && execution.runResults && execution.runResults.length > 0) {
        // First, try to append the newest assistant message from run results
        try {
          const latest = [...execution.runResults]
            .filter(r => r.step_type === 'run_agent' && r.orchestration_step_id === assistantStep.orchestration_step_id)
            .sort((a,b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())
            .pop();
          if (latest) {
            let content: string | null = null;
            if (latest.as_input_list_items && latest.as_input_list_items.length > 0) {
              const lastItem = latest.as_input_list_items[latest.as_input_list_items.length - 1];
              const data = lastItem.item_data;
              if (data && data.role === 'assistant' && data.content) {
                if (typeof data.content === 'string') content = data.content;
                else if (Array.isArray(data.content)) {
                  const t = data.content.find((c: any) => c.type === 'output_text' || c.type === 'text');
                  content = t ? t.text : JSON.stringify(data.content);
                } else content = JSON.stringify(data.content);
              }
            }
            if (!content && latest.final_output) {
              if (typeof latest.final_output === 'string') content = latest.final_output;
              else if (Array.isArray(latest.final_output as any)) {
                const t = (latest.final_output as any).find((c: any) => c.type === 'output_text' || c.type === 'text');
                content = t ? t.text : JSON.stringify(latest.final_output);
              } else if (typeof latest.final_output === 'object') {
                const out: any = latest.final_output;
                content = out.content || out.message || JSON.stringify(out);
              }
            }

            if (content) {
              const current = useAssistantChatStore.getState();
              const key = current.activeSessionKey;
              const exists = key && (current.messagesBySession[key] || []).some(m => m.role === 'assistant' && m.content === content);
              if (!exists) {
                current.addMessage({
                  id: `assistant-${Date.now()}`,
                  role: 'assistant',
                  content,
                  timestamp: latest.created_at || new Date().toISOString()
                });
              }
            }
          }
        } catch (e) {
          console.log('[DEBUG] ChatInterface - append-from-run-results error', e);
        }

        // Reload the full history only if there are no messages yet (initial load)
        const currentMessages = getMessages();
        const shouldReload = currentMessages.length === 0;
        if (shouldReload) {
          console.log('[DEBUG] ChatInterface - Run results changed, reloading conversation history');
          setTimeout(() => {
            loadConversationHistory();
          }, 100);
        }
      }
    });

    return unsubscribe;
  }, [workflow.orchestration_id, loadConversationHistory, activeSessionKey, getMessages, assistantStep.orchestration_step_id]);

  // Append-on-new: when execution store reports new run results, append just the newest assistant/user message
  useEffect(() => {
    const execution = useWorkflowStore.getState().executions.get(String(workflow.orchestration_id));
    if (!execution || !execution.runResults || execution.runResults.length === 0) return;

    // Find the latest run_agent with final_output or items
    const latest = [...execution.runResults]
      .filter(r => r.step_type === 'run_agent' && r.orchestration_step_id === assistantStep.orchestration_step_id)
      .sort((a,b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())
      .pop();

    if (!latest) return;

    let content: string | null = null;
    if (latest.as_input_list_items && latest.as_input_list_items.length > 0) {
      const lastItem = latest.as_input_list_items[latest.as_input_list_items.length - 1];
      const data = lastItem.item_data;
      if (data && data.role && data.content) {
        if (typeof data.content === 'string') content = data.content;
        else if (Array.isArray(data.content)) {
          const t = data.content.find((c: any) => c.type === 'output_text' || c.type === 'text');
          content = t ? t.text : JSON.stringify(data.content);
        } else content = JSON.stringify(data.content);
      }
    } else if (latest.final_output) {
      if (typeof latest.final_output === 'string') content = latest.final_output;
      else if (Array.isArray(latest.final_output as any)) {
        const t = (latest.final_output as any).find((c: any) => c.type === 'output_text' || c.type === 'text');
        content = t ? t.text : JSON.stringify(latest.final_output);
      } else if (typeof latest.final_output === 'object') {
        const out: any = latest.final_output;
        content = out.content || out.message || JSON.stringify(out);
      }
    }

    if (!content) return;

    const current = useAssistantChatStore.getState();
    const key = current.activeSessionKey;
    if (!key) return;
    const existing = (current.messagesBySession[key] || []).some(m => m.role === 'assistant' && m.content === content);
    if (existing) return;

    // Append the new assistant message without clearing
    current.addMessage({
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content,
      timestamp: latest.created_at || new Date().toISOString()
    });
  }, [workflow.orchestration_id, assistantStep.orchestration_step_id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    console.log('[DEBUG] ChatInterface - handleSend called, input:', input.trim(), 'isLoading:', isLoading, 'isWorkflowRunning:', isWorkflowRunning);
    if (!input.trim() || isLoading) {
      console.log('[DEBUG] ChatInterface - handleSend aborted due to empty input or loading');
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      role: 'user' as const,
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    console.log('[DEBUG] ChatInterface - Adding user message:', userMessage);
    // Add user message to store
    addMessage(userMessage);
    setInput('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      console.log('[DEBUG] ChatInterface - Calling onMessageSend with message:', input.trim());
      // Send message through workflow
      await onMessageSend(input.trim());

      // The assistant's response will be added through the workflow flow
      // and picked up by the store subscription
    } catch (error) {
      console.error('Failed to send message:', error);

      // Add error message
      addMessage({
        id: Date.now().toString(),
        role: 'system' as const,
        content: 'Failed to send message. Please try again.',
        timestamp: new Date().toISOString(),
        metadata: { error: true }
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-cap-dark rounded-lg shadow-lg ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {assistantStep.label || 'AI Assistant'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {assistantStep.description || 'Chat with the AI assistant'}
            </p>
          </div>
          <ChatViewClearButton />
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-h-0">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p>Loading conversation...</p>
            </div>
          </div>
        ) : (
          <MessageList
            messages={messages.map(msg => ({
              ...msg,
              id: msg.id || `msg-${Date.now()}-${Math.random()}`,
              timestamp: msg.timestamp || new Date().toISOString()
            }))}
            isTyping={isTyping}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex-shrink-0">
        <MessageInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          onKeyPress={handleKeyPress}
          disabled={isLoading || !isWorkflowRunning}
          placeholder={
            !isWorkflowRunning
              ? "Start the workflow to chat..."
              : "Type your message..."
          }
        />

        {/* Workflow not running indicator */}
        {!isWorkflowRunning && (
          <div className="mt-2 p-3 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Start the workflow to begin chatting with the assistant</span>
            </div>
          </div>
        )}

        {/* Status indicators */}
        {messages.length > 0 && isWorkflowRunning && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {messages.filter(m => m.role === 'user').length} messages in conversation
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;